import { UseFormReturn } from 'react-hook-form';
import { EventPanelFormType } from '@mark43/cad-api';
import { useEnabledFeatures } from '../../../common/hooks/enabled-features/use-enabled-features';
import { useWorkspacePanel } from '../../../panels/hooks/use-workspace-panel';
import { useFormHasHadDataSetAtLeastOnceContext } from '../../event-state/form-has-had-data-set-at-least-once';
import { useEventMetadataStore } from '../../event-panel-form/event-metadata';
import {
    useAutoFillEventLocationField,
    AutoFillLocationStatus,
} from '../../event-panel-form/use-auto-fill-event-location-field';
import { usePhoneCallForEvent } from './get-phone-call-for-event';
import { shouldAutoImportCallerInfoOnE911 } from './auto-import-caller-info-helpers';
import { buildCallLocationQuery } from './auto-import-location-helpers';

export const LOCATION_PREFILLED_MESSAGE = 'Prefilled with ANI/ALI caller location';
export const LOCATION_PREFILL_FAILED_MESSAGE =
    'Location could not be prefilled. Enter location manually.';

/**
 * When the "Automatically import caller name and location on E911 calls" department
 * preference is enabled and a draft event panel opens from an incoming E911 call, resolve
 * the caller's location from the ANI/ALI data and fill the event's Location field — no
 * picker, one time.
 *
 * This hook is only the E911 policy: it decides *when* to fill (pref + E911 origin +
 * unsaved draft + form initialized + a resolved call) and *what* query to resolve (GPS →
 * reverse geocode, else the caller address; `chooseCallLocationSource` mirrors
 * `useCallImport`'s `importGpsOrAddress` precedence). All the generic machinery — resolve,
 * classify, silent write, cancel-on-edit, timeout, and status — lives in
 * `useAutoFillEventLocationField`.
 *
 * Gating on `originIsE911` + the preference means manual event creation and pref-off
 * departments are unaffected; gating on the form-initialized signal means the fill lands
 * after the panel's deferred initial reset (so it isn't clobbered). Reporting-party
 * name/phone import is a separate hook, so a location failure never blocks it.
 *
 * Returns the current status so the location field can render the spinner / blurb.
 */
export function useAutoImportLocationOnE911(
    form: UseFormReturn<EventPanelFormType>,
    onLocationFilled: () => void
): AutoFillLocationStatus {
    const {
        autoImportAniAliOnE911Call,
        preferPhysicalAddressForVoip911Calls,
        subpremiseSupport,
    } = useEnabledFeatures();
    const eventExists = !!useEventMetadataStore()?.state.metadata?.eventId;
    const originIsE911 = !!useWorkspacePanel<'EVENT'>().getPanelState('originIsE911');
    const isFormInitialized = useFormHasHadDataSetAtLeastOnceContext();
    const selectedCall = usePhoneCallForEvent();

    const enabled = shouldAutoImportCallerInfoOnE911({
        isPreferenceEnabled: autoImportAniAliOnE911Call,
        isE911Origin: originIsE911,
        eventExists,
        hasSelectedCall: !!selectedCall,
        isFormInitialized,
    });
    // Only derive the query when we would actually fill — nothing to resolve otherwise.
    const query = enabled
        ? buildCallLocationQuery(selectedCall, {
              preferPhysicalAddressForVoip911Calls,
              subpremiseSupport,
          })
        : null;

    return useAutoFillEventLocationField({ form, query, enabled, onLocationFilled });
}
